var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useStableOkapiKy } from "./useStableOkapiKy";
import { usePublisherBySourceId } from "./usePublisherBySourceId";
import { agencyPrefixSearchQuery, publisherPrefixRangeSearchQuery, } from "../utils/prefixSearchQueries";
var INITIAL_FILTERS = {
    value: "",
    agencyPrefixId: "",
    publisherPrefixRangeId: "",
    publisherId: "",
    showClosed: false,
};
/**
 * The applied filters as the URL holds them; a publisher user's own publisher
 * replaces whatever the URL says.
 *
 * @param search `location.search` of the list
 * @param initialPublisherId the publisher user's own publisher (sourceId), if any
 */
var filtersFromSearch = function (search, initialPublisherId) {
    var _a, _b, _c, _d;
    var queryParams = new URLSearchParams(search);
    return {
        value: (_a = queryParams.get("value")) !== null && _a !== void 0 ? _a : "",
        agencyPrefixId: (_b = queryParams.get("agencyPrefixId")) !== null && _b !== void 0 ? _b : "",
        publisherPrefixRangeId: (_c = queryParams.get("publisherPrefixRangeId")) !== null && _c !== void 0 ? _c : "",
        publisherId: (_d = initialPublisherId !== null && initialPublisherId !== void 0 ? initialPublisherId : queryParams.get("publisherId")) !== null && _d !== void 0 ? _d : "",
        showClosed: queryParams.get("showClosed") === "1",
    };
};
var DEBOUNCE_DELAY = 300;
/** Options shown by the agency prefix / range typeaheads. */
var TYPEAHEAD_LIMIT = "20";
var createAgencyPrefixLabel = function (agencyPrefix) {
    var _a, _b, _c, _d, _e;
    var agency = (_b = (_a = agencyPrefix.internationalPrefix) === null || _a === void 0 ? void 0 : _a.agency) !== null && _b !== void 0 ? _b : "";
    var internationalValue = (_d = (_c = agencyPrefix.internationalPrefix) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : "";
    var agencyValue = (_e = agencyPrefix.value) !== null && _e !== void 0 ? _e : "";
    return "".concat(agency, " ").concat(internationalValue, "-").concat(agencyValue).trim();
};
var createPublisherPrefixRangeLabel = function (range) {
    var _a, _b;
    var agencyValue = (_b = (_a = range.agencyPrefix) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : "";
    return "".concat(agencyValue, " ").concat(range.start, "-").concat(range.end);
};
export var usePublisherPrefixFilters = function (initialPublisherId, isPublisherScopePending) {
    var history = useHistory();
    var location = useLocation();
    var ky = useStableOkapiKy();
    // Initialised from the URL in the first render: filters patched in by an
    // effect afterwards would change the search and send it twice.
    var _a = useState(function () {
        return filtersFromSearch(location.search, initialPublisherId);
    }), filters = _a[0], setFilters = _a[1];
    var _b = useState(filters), appliedFilters = _b[0], setAppliedFilters = _b[1];
    var _c = useState([]), agencyPrefixOptions = _c[0], setAgencyPrefixOptions = _c[1];
    var _d = useState([]), publisherPrefixRangeOptions = _d[0], setPublisherPrefixRangeOptions = _d[1];
    var _e = useState(false), isAgencyPrefixLoading = _e[0], setIsAgencyPrefixLoading = _e[1];
    var _f = useState(false), isPublisherPrefixRangeLoading = _f[0], setIsPublisherPrefixRangeLoading = _f[1];
    var _g = useState(""), publisherQuery = _g[0], setPublisherQuery = _g[1];
    var _h = useState(filters.publisherId), temporaryPublisherId = _h[0], setTemporaryPublisherId = _h[1];
    var _j = useState(null), selectedAgencyOption = _j[0], setSelectedAgencyOption = _j[1];
    var _k = useState(null), selectedRangeOption = _k[0], setSelectedRangeOption = _k[1];
    var selectedPublisher = usePublisherBySourceId(temporaryPublisherId || undefined).data;
    var updateUrlWithFilters = function (nextFilters) {
        var searchParams = new URLSearchParams();
        if (nextFilters.value)
            searchParams.set("value", nextFilters.value);
        if (nextFilters.agencyPrefixId)
            searchParams.set("agencyPrefixId", nextFilters.agencyPrefixId);
        if (nextFilters.publisherPrefixRangeId)
            searchParams.set("publisherPrefixRangeId", nextFilters.publisherPrefixRangeId);
        if (nextFilters.publisherId)
            searchParams.set("publisherId", nextFilters.publisherId);
        if (nextFilters.showClosed)
            searchParams.set("showClosed", "1");
        history.push({ search: searchParams.toString() });
    };
    var escapeCql = function (input) {
        return input.replace(/(["\\])/g, "\\$1");
    };
    var extractItemsFromResponse = function (data, key) {
        var _a;
        var items = (_a = data.items) !== null && _a !== void 0 ? _a : data[key];
        return Array.isArray(items) ? items : [];
    };
    var fetchAgencyPrefixById = function (id) { return __awaiter(void 0, void 0, void 0, function () {
        var resp, _a, searchParams, resp, data, list, _b;
        var _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    if (!id)
                        return [2 /*return*/, null];
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, ky.get("agency-prefixes/".concat(encodeURIComponent(id)))];
                case 2:
                    resp = _d.sent();
                    if (!resp.ok) return [3 /*break*/, 4];
                    return [4 /*yield*/, resp.json()];
                case 3: return [2 /*return*/, (_d.sent())];
                case 4: return [3 /*break*/, 6];
                case 5:
                    _a = _d.sent();
                    return [3 /*break*/, 6];
                case 6:
                    _d.trys.push([6, 9, , 10]);
                    searchParams = new URLSearchParams();
                    searchParams.set("query", "id==\"".concat(escapeCql(id), "\""));
                    searchParams.set("limit", "1");
                    return [4 /*yield*/, ky.get("agency-prefixes", { searchParams: searchParams })];
                case 7:
                    resp = _d.sent();
                    return [4 /*yield*/, resp.json()];
                case 8:
                    data = (_d.sent());
                    list = extractItemsFromResponse(data, "agencyPrefixes");
                    return [2 /*return*/, (_c = list[0]) !== null && _c !== void 0 ? _c : null];
                case 9:
                    _b = _d.sent();
                    return [2 /*return*/, null];
                case 10: return [2 /*return*/];
            }
        });
    }); };
    var fetchPublisherPrefixRangeById = function (id) { return __awaiter(void 0, void 0, void 0, function () {
        var resp, _a, searchParams, resp, data, list, _b;
        var _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    if (!id)
                        return [2 /*return*/, null];
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, ky.get("publisher-prefix-ranges/".concat(encodeURIComponent(id)))];
                case 2:
                    resp = _d.sent();
                    if (!resp.ok) return [3 /*break*/, 4];
                    return [4 /*yield*/, resp.json()];
                case 3: return [2 /*return*/, (_d.sent())];
                case 4: return [3 /*break*/, 6];
                case 5:
                    _a = _d.sent();
                    return [3 /*break*/, 6];
                case 6:
                    _d.trys.push([6, 9, , 10]);
                    searchParams = new URLSearchParams();
                    searchParams.set("query", "id==\"".concat(escapeCql(id), "\""));
                    searchParams.set("limit", "1");
                    return [4 /*yield*/, ky.get("publisher-prefix-ranges", { searchParams: searchParams })];
                case 7:
                    resp = _d.sent();
                    return [4 /*yield*/, resp.json()];
                case 8:
                    data = (_d.sent());
                    list = extractItemsFromResponse(data, "publisherPrefixRanges");
                    return [2 /*return*/, (_c = list[0]) !== null && _c !== void 0 ? _c : null];
                case 9:
                    _b = _d.sent();
                    return [2 /*return*/, null];
                case 10: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        var queryParams = new URLSearchParams(location.search);
        var urlPublisherId = queryParams.get("publisherId");
        var shouldCorrectPublisherId = initialPublisherId && urlPublisherId !== initialPublisherId;
        var next = filtersFromSearch(location.search, initialPublisherId);
        setFilters(function (prev) { return (isEqual(prev, next) ? prev : next); });
        setAppliedFilters(function (prev) { return (isEqual(prev, next) ? prev : next); });
        setTemporaryPublisherId(next.publisherId);
        if (shouldCorrectPublisherId) {
            updateUrlWithFilters(next);
        }
        (function () { return __awaiter(void 0, void 0, void 0, function () {
            var ap, range;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!next.agencyPrefixId) return [3 /*break*/, 2];
                        return [4 /*yield*/, fetchAgencyPrefixById(next.agencyPrefixId)];
                    case 1:
                        ap = _a.sent();
                        setSelectedAgencyOption({
                            value: next.agencyPrefixId,
                            label: ap ? createAgencyPrefixLabel(ap) : next.agencyPrefixId,
                        });
                        return [3 /*break*/, 3];
                    case 2:
                        setSelectedAgencyOption(null);
                        _a.label = 3;
                    case 3:
                        if (!next.publisherPrefixRangeId) return [3 /*break*/, 5];
                        return [4 /*yield*/, fetchPublisherPrefixRangeById(next.publisherPrefixRangeId)];
                    case 4:
                        range = _a.sent();
                        setSelectedRangeOption({
                            value: next.publisherPrefixRangeId,
                            label: range
                                ? createPublisherPrefixRangeLabel(range)
                                : next.publisherPrefixRangeId,
                        });
                        return [3 /*break*/, 6];
                    case 5:
                        setSelectedRangeOption(null);
                        _a.label = 6;
                    case 6: return [2 /*return*/];
                }
            });
        }); })();
    }, [location.search, initialPublisherId]); // eslint-disable-line react-hooks/exhaustive-deps
    var isQueryReady = useMemo(function () {
        if (isPublisherScopePending)
            return false;
        return !(initialPublisherId && appliedFilters.publisherId !== initialPublisherId);
    }, [isPublisherScopePending, initialPublisherId, appliedFilters.publisherId]);
    var handleFilterChange = function (field, value) {
        var _a;
        var _b, _c;
        var next = __assign(__assign({}, filters), (_a = {}, _a[field] = value, _a));
        if (field === "agencyPrefixId") {
            next.publisherPrefixRangeId = "";
            setSelectedRangeOption(null);
            if (typeof value === "string" && value) {
                var foundOption = agencyPrefixOptions.find(function (o) { return o.value === value; });
                setSelectedAgencyOption({
                    value: value,
                    label: (_b = foundOption === null || foundOption === void 0 ? void 0 : foundOption.label) !== null && _b !== void 0 ? _b : value,
                });
            }
            else {
                setSelectedAgencyOption(null);
            }
        }
        if (field === "publisherPrefixRangeId" && typeof value === "string") {
            if (value) {
                var foundOption = publisherPrefixRangeOptions.find(function (o) { return o.value === value; });
                setSelectedRangeOption({
                    value: value,
                    label: (_c = foundOption === null || foundOption === void 0 ? void 0 : foundOption.label) !== null && _c !== void 0 ? _c : value,
                });
            }
            else {
                setSelectedRangeOption(null);
            }
        }
        if (field === "publisherId" && typeof value === "string") {
            setTemporaryPublisherId(value);
        }
        setFilters(next);
    };
    var handleApplyFilters = function () {
        var publisherSourceId = temporaryPublisherId && (selectedPublisher === null || selectedPublisher === void 0 ? void 0 : selectedPublisher.sourceId)
            ? selectedPublisher.sourceId
            : temporaryPublisherId;
        var updatedFilters = __assign(__assign({}, filters), { publisherId: publisherSourceId });
        setAppliedFilters(updatedFilters);
        updateUrlWithFilters(updatedFilters);
    };
    var handleClearFilters = function () {
        setFilters(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
        setSelectedAgencyOption(null);
        setSelectedRangeOption(null);
        setTemporaryPublisherId("");
        history.push({ search: "" });
    };
    var isAnyFilterActive = function () {
        return Boolean(appliedFilters.value ||
            appliedFilters.agencyPrefixId ||
            appliedFilters.publisherPrefixRangeId ||
            appliedFilters.publisherId ||
            appliedFilters.showClosed);
    };
    var hasUnappliedChanges = function () {
        return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
    };
    var buildQuery = function (filterValues) {
        var _a;
        if (filterValues === void 0) { filterValues = appliedFilters; }
        var queryParts = [];
        var trimmedValue = (_a = filterValues.value) === null || _a === void 0 ? void 0 : _a.trim();
        if (trimmedValue) {
            queryParts.push("value==\"*".concat(escapeCql(trimmedValue), "*\""));
        }
        if (filterValues.publisherId) {
            queryParts.push("publisher.sourceId==\"".concat(escapeCql(filterValues.publisherId), "\""));
        }
        if (filterValues.publisherPrefixRangeId) {
            queryParts.push("publisherPrefixRange.id==\"".concat(escapeCql(filterValues.publisherPrefixRangeId), "\""));
        }
        if (filterValues.agencyPrefixId) {
            queryParts.push("publisherPrefixRange.agencyPrefix.id==\"".concat(escapeCql(filterValues.agencyPrefixId), "\""));
        }
        if (!filterValues.showClosed) {
            queryParts.push("status==\"ACTIVE\"");
        }
        var baseQuery = queryParts.length > 0 ? queryParts.join(" and ") : "cql.allRecords=1";
        return "".concat(baseQuery, " sortBy status value");
    };
    var handleAgencyPrefixInput = useMemo(function () {
        return debounce(function (searchText) { return __awaiter(void 0, void 0, void 0, function () {
            var trimmed, response, responseData, list, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, 4, 5]);
                        trimmed = searchText === null || searchText === void 0 ? void 0 : searchText.trim();
                        if (!trimmed) {
                            setAgencyPrefixOptions([]);
                            return [2 /*return*/];
                        }
                        setIsAgencyPrefixLoading(true);
                        return [4 /*yield*/, ky.get("agency-prefixes", {
                                searchParams: {
                                    query: agencyPrefixSearchQuery(trimmed),
                                    limit: TYPEAHEAD_LIMIT,
                                },
                            })];
                    case 1:
                        response = _b.sent();
                        return [4 /*yield*/, response.json()];
                    case 2:
                        responseData = (_b.sent());
                        list = extractItemsFromResponse(responseData, "agencyPrefixes");
                        setAgencyPrefixOptions(list.map(function (ap) { return ({
                            value: ap.id,
                            label: createAgencyPrefixLabel(ap),
                        }); }));
                        return [3 /*break*/, 5];
                    case 3:
                        _a = _b.sent();
                        setAgencyPrefixOptions([]);
                        return [3 /*break*/, 5];
                    case 4:
                        setIsAgencyPrefixLoading(false);
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }); }, DEBOUNCE_DELAY);
    }, [ky]);
    var handlePublisherPrefixRangeInput = useMemo(function () {
        return debounce(function (searchText) { return __awaiter(void 0, void 0, void 0, function () {
            var trimmed, response, responseData, list, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, 4, 5]);
                        trimmed = searchText === null || searchText === void 0 ? void 0 : searchText.trim();
                        if (!trimmed) {
                            setPublisherPrefixRangeOptions([]);
                            return [2 /*return*/];
                        }
                        setIsPublisherPrefixRangeLoading(true);
                        return [4 /*yield*/, ky.get("publisher-prefix-ranges", {
                                searchParams: {
                                    query: publisherPrefixRangeSearchQuery(trimmed),
                                    limit: TYPEAHEAD_LIMIT,
                                },
                            })];
                    case 1:
                        response = _b.sent();
                        return [4 /*yield*/, response.json()];
                    case 2:
                        responseData = (_b.sent());
                        list = extractItemsFromResponse(responseData, "publisherPrefixRanges");
                        setPublisherPrefixRangeOptions(list.map(function (range) { return ({
                            value: range.id,
                            label: createPublisherPrefixRangeLabel(range),
                        }); }));
                        return [3 /*break*/, 5];
                    case 3:
                        _a = _b.sent();
                        setPublisherPrefixRangeOptions([]);
                        return [3 /*break*/, 5];
                    case 4:
                        setIsPublisherPrefixRangeLoading(false);
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }); }, DEBOUNCE_DELAY);
    }, [ky]);
    var handlePublisherInput = useMemo(function () {
        return debounce(function (searchText) {
            if (searchText !== publisherQuery) {
                setPublisherQuery(searchText);
            }
        }, DEBOUNCE_DELAY);
    }, [publisherQuery]);
    return {
        filters: filters,
        appliedFilters: appliedFilters,
        publisherQuery: publisherQuery,
        temporaryPublisherId: temporaryPublisherId,
        selectedPublisher: selectedPublisher,
        handleFilterChange: handleFilterChange,
        handleApplyFilters: handleApplyFilters,
        handleClearFilters: handleClearFilters,
        isAnyFilterActive: isAnyFilterActive,
        hasUnappliedChanges: hasUnappliedChanges,
        buildQuery: buildQuery,
        isQueryReady: isQueryReady,
        handleAgencyPrefixInput: handleAgencyPrefixInput,
        handlePublisherPrefixRangeInput: handlePublisherPrefixRangeInput,
        handlePublisherInput: handlePublisherInput,
        agencyPrefixOptions: agencyPrefixOptions,
        publisherPrefixRangeOptions: publisherPrefixRangeOptions,
        isAgencyPrefixLoading: isAgencyPrefixLoading,
        isPublisherPrefixRangeLoading: isPublisherPrefixRangeLoading,
        selectedAgencyOption: selectedAgencyOption,
        selectedRangeOption: selectedRangeOption,
    };
};
